import { useEffect } from 'react';

import type { ApiListingApplicationId } from '#/api/types/ApiTypedId';
import { Box } from '#/components/Box/Box';
import { VolopDetailsList } from '#/components/DetailsList/Volop/VolopDetailsList';
import { PageToolbar } from '#/components/PageToolbar/PageToolbar';
import { PrintHidden } from '#/components/PrintHidden/PrintHidden';
import { useUser } from '#/contexts/UserEnvironmentContext';
import { useI18n } from '#/i18n/hooks/useI18n';
import { trackViewListing } from '#/modules/listing/analytics/trackViewListing';
import { ListingViewAdminTools } from '#/modules/listing/components/ListingView/AdminTools/ListingViewAdminTools';
import { ListingViewBody } from '#/modules/listing/components/ListingView/Body/ListingViewBody';
import { ListingViewExpiredTag } from '#/modules/listing/components/ListingView/ExpiredTag/ListingViewExpiredTag';
import { ListingViewFooter } from '#/modules/listing/components/ListingView/Footer/ListingViewFooter';
import { ListingViewHeader } from '#/modules/listing/components/ListingView/Header/ListingViewHeader';
import { ListingViewHeaderSearchContext } from '#/modules/listing/components/ListingView/HeaderSearchContext/ListingViewHeaderSearchContext';
import { ListingViewMain } from '#/modules/listing/components/ListingView/Main/ListingViewMain';
import { ListingViewSection } from '#/modules/listing/components/ListingView/Section/ListingViewSection';
import { ListingViewSidebar } from '#/modules/listing/components/ListingView/Sidebar/ListingViewSidebar';
import { ListingViewSidebarContent } from '#/modules/listing/components/ListingView/Sidebar/ListingViewSidebarContent';
import { ListingViewSidebarImage } from '#/modules/listing/components/ListingView/Sidebar/ListingViewSidebarImage';
import { ListingViewSidebarLinks } from '#/modules/listing/components/ListingView/Sidebar/ListingViewSidebarLinks';
import { ListingViewSidebarTitle } from '#/modules/listing/components/ListingView/Sidebar/ListingViewSidebarTitle';
import { ListingViewSignupCta } from '#/modules/listing/components/ListingView/SignupCta/ListingViewSignupCta';
import { ListingViewDescriptionSection } from '#/modules/listing/components/ListingView/sections/Description/ListingViewDescriptionSection';
import { ListingViewLocationSection } from '#/modules/listing/components/ListingView/sections/ListingViewLocationSection';
import { ListingViewStatus } from '#/modules/listing/types/ListingViewStatus';
import type { ApiVolop } from '#/modules/listing/volops/api/types/ApiVolop';
import { SearchContextHeader } from '#/modules/search/components/Context/SearchContextHeader';
import { useSearchContextHeader } from '#/modules/search/containers/SearchContextHeader/useSearchContextHeader';
import { trackClicked } from '#/utils/analytics/track/trackClicked';
import { isUserOrgOwner } from '#/utils/listings/getOrgOwner';
import { legacyIsExternalURL } from '#/utils/url/isExternalURL';
import { showModal } from '#/zustand-stores/modalStore';
import { dismissToast, showToast } from '#/zustand-stores/toastsStore';

type Props = {
  volop: ApiVolop;
  listingApplicationId: ApiListingApplicationId | null;
};

export function ExpiredVolopPage({ volop, listingApplicationId }: Props) {
  const { i18n, locale } = useI18n();

  const {
    address,
    areasOfFocus,
    id,
    hasAts,
    startDate,
    directions,
    endDate,
    startsLocal,
    endsLocal,
    functions,
    firstPublished,
    driversLicenseRequired,
    backgroundCheckRequired,
    orientationRequired,
    feeRequired,
    feeAmount,
    isPostedAnonymously,
    isRecurring,
    name,
    timezone,
    welcomeFamilies,
    welcomeTeens,
    welcomeGroups,
    welcomeAge55Plus,
    welcomeIntl,
    welcomePrivateCorpGroups,
    detailsStipendProvided,
    detailsCulturalSupport,
    detailsHousingAvailable,
    detailsTrainingProvided,
    detailsAcademicCreditAvailable,
    detailsWheelchairAccessible,
    timesOfDay,
    type,
    image,
    description,
    descriptionTruncated,
    expectedTime,
    remoteOptions,
    url,
    org,
    ageRequirement,
    otherRequirements,
    numVolunteersNeeded,
    commitmentDetails,
    expires,
  } = volop;

  const applied = Boolean(listingApplicationId);

  const { searchContext } = useSearchContextHeader({ currentObjectId: id });

  const { user } = useUser();
  const isOrgOwner = isUserOrgOwner(user, org?.id);

  useEffect(() => {
    trackViewListing({
      status: 'expired',
      hasAts,
      listingType: type,
      listingId: id,
      listingName: name,
      orgId: org?.id,
      orgName: org?.name,
      orgType: org?.orgType,
    });
  }, [hasAts, id, name, org?.id, org?.name, org?.orgType, type]);

  useEffect(() => {
    const toastId = showToast({
      headline: i18n('This listing has expired'),
      body: i18n(
        'This listing has expired and is no longer accepting applications.',
      ),
      dismissible: true,
      variant: 'warning',
    });

    return () => {
      dismissToast(toastId);
    };
  }, [i18n]);

  const orgLogo = org?.logo ?? null;
  const orgName = org?.name ?? null;
  const orgUrl = org?.url[locale] ?? null;
  const orgType = org?.orgType ?? null;
  const expiredAt = Date.parse(expires) ? new Date(expires) : undefined;

  return (
    <>
      {isOrgOwner && org && (
        <PageToolbar
          centered
          message={i18n('Your organization posted this volunteer opportunity')}
          tools={
            <ListingViewAdminTools
              listingId={id}
              listingType="VOLOP"
              orgId={org.id}
              canPost={Boolean(
                user?.orgs.find((o) => o.id === org.id)?.canPostVolops,
              )}
              hasAts={hasAts}
            />
          }
        />
      )}

      <ListingViewBody>
        <ListingViewSidebar maxWidth={288}>
          <ListingViewSidebarImage
            orgLogo={orgLogo}
            orgName={orgName}
            orgURLForCurrentLocale={orgUrl}
          />
          <ListingViewSidebarTitle
            address={address}
            listingName={name}
            listingType="VOLOP"
            orgName={orgName}
            orgUrl={orgUrl}
            remoteOptions={remoteOptions}
          />
          <ListingViewSidebarContent
            firstPublished={firstPublished}
            listingType={type}
            showScrollToApplyButton={hasAts}
            applied={applied}
            listingViewStatus={ListingViewStatus.EXPIRED}
          />
          <ListingViewSidebarLinks
            searchContext={searchContext}
            orgUrl={orgUrl}
          />
        </ListingViewSidebar>

        <ListingViewMain>
          {searchContext && (
            <ListingViewHeaderSearchContext searchContext={searchContext} />
          )}

          <ListingViewExpiredTag expiredAt={expiredAt} />

          <ListingViewHeader
            address={address}
            isPostedAnonymously={isPostedAnonymously}
            listingId={id}
            listingName={name}
            listingType={type}
            listingFirstPublished={firstPublished}
            orgName={orgName}
            orgType={orgType}
            orgUrl={orgUrl}
            remoteOptions={remoteOptions}
            listingViewStatus={ListingViewStatus.EXPIRED}
            actionButtons={null}
          />

          <ListingViewSection title={i18n('Details')} noBorderTop>
            <VolopDetailsList
              areasOfFocus={areasOfFocus}
              startDate={startDate}
              endDate={endDate}
              startsLocal={startsLocal}
              endsLocal={endsLocal}
              driversLicenseRequired={driversLicenseRequired}
              backgroundCheckRequired={backgroundCheckRequired}
              orientationRequired={orientationRequired}
              feeRequired={feeRequired}
              feeAmount={feeAmount}
              isRecurring={isRecurring}
              timezone={timezone}
              welcomeFamilies={welcomeFamilies}
              welcomeTeens={welcomeTeens}
              welcomeGroups={welcomeGroups}
              welcomeAge55Plus={welcomeAge55Plus}
              welcomeIntl={welcomeIntl}
              welcomePrivateCorpGroups={welcomePrivateCorpGroups}
              detailsStipendProvided={detailsStipendProvided}
              detailsTrainingProvided={detailsTrainingProvided}
              detailsHousingAvailable={detailsHousingAvailable}
              detailsCulturalSupport={detailsCulturalSupport}
              detailsAcademicCreditAvailable={detailsAcademicCreditAvailable}
              detailsWheelchairAccessible={detailsWheelchairAccessible}
              timesOfDay={timesOfDay}
              expectedTime={expectedTime}
              ageRequirement={ageRequirement}
              otherRequirements={otherRequirements}
              numVolunteersNeeded={numVolunteersNeeded}
              commitmentDetails={commitmentDetails}
            />
          </ListingViewSection>

          <ListingViewDescriptionSection
            listingName={name}
            image={image}
            description={description}
            descriptionTruncated={descriptionTruncated}
            handleShowEmailModal={(link) => {
              if (!user && legacyIsExternalURL(link.href)) {
                showModal('EMAIL_ALERT', {
                  listingFunctions: functions,
                  orgId: org?.id,
                  listingName: name,
                  listingType: 'VOLOP',
                });
              }
            }}
            showMoreLinkTitle={i18n(
              'Read more about this volunteer opportunity',
            )}
            onToggleDetails={(isOpen) => {
              if (!isOpen) return;

              trackClicked('read more about this listing', {
                listing_type: volop.type,
                locale: volop.locale,
              });
            }}
          />

          <ListingViewLocationSection
            listingType={type}
            address={address}
            directions={directions}
            remoteOptions={remoteOptions}
          />

          {searchContext && (
            <PrintHidden>
              <Box mt={70} mb={48}>
                <SearchContextHeader searchContext={searchContext} noDivider />
              </Box>
            </PrintHidden>
          )}
        </ListingViewMain>
      </ListingViewBody>

      {!user && (
        <PrintHidden>
          <ListingViewFooter>
            <ListingViewSignupCta listingUrl={url[locale]} type={type} />
          </ListingViewFooter>
        </PrintHidden>
      )}
    </>
  );
}
